// ContainerReassignModal
// Shown when deleting a non-empty container (locker or bin).
// Displays a recursive tree of everything inside.
// Each item and bin gets: Move To (container picker) or Delete.
// Only once all contents are resolved does the Delete button enable.

import { useState, useEffect } from 'react'
import { IconBox, IconLayoutBottombar, IconLock, IconChevronRight, IconChevronDown, IconTrash, IconArrowRight } from '@tabler/icons-react'
import { containersApi, gearItemsApi, type ContainerNode, type GearItemNode, type Container } from '../../lib/api.ts'
import { useToast } from './Toast.tsx'

type Resolution = { action: 'move'; targetId: string } | { action: 'delete' }

type Props = {
  container: Container
  allContainers: Container[] // flat list of all user containers for the move picker
  onResolved: () => void     // called when all contents have been handled and container can now be deleted
  onCancel: () => void
}

// Flatten the allContainers list excluding the container being deleted and its descendants
function getValidTargets(allContainers: Container[], excludeId: string): Container[] {
  // Build a set of all descendant IDs to exclude
  const excluded = new Set<string>([excludeId])
  let changed = true
  while (changed) {
    changed = false
    for (const c of allContainers) {
      if (c.parentId && excluded.has(c.parentId) && !excluded.has(c.id)) {
        excluded.add(c.id)
        changed = true
      }
    }
  }
  return allContainers.filter(c => !excluded.has(c.id))
}

function containerIcon(c: Container) {
  if (c.isTopLevel) return <IconLock size={13} className="text-zinc-400 shrink-0" />
  return <IconLayoutBottombar size={13} className="text-zinc-400 shrink-0" />
}

function containerLabel(c: Container) {
  return c.name
}

// ── Container picker dropdown ─────────────────────────────────────────────────
function ContainerPicker({
  value, onChange, targets, placeholder = 'Move to...'
}: {
  value: string; onChange: (id: string) => void; targets: Container[]; placeholder?: string
}) {
  // Build indented display: sort by top-level first, then by parentId chain
  const lockers = targets.filter(t => t.isTopLevel)
  const bins = targets.filter(t => !t.isTopLevel)

  const indent = (id: string, depth = 0): number => {
    const c = targets.find(t => t.id === id)
    if (!c || !c.parentId) return depth
    return indent(c.parentId, depth + 1)
  }

  // Sort bins by parentage for logical grouping
  const sortedBins = [...bins].sort((a, b) => {
    const da = indent(a.id)
    const db = indent(b.id)
    return da - db || a.name.localeCompare(b.name)
  })

  return (
    <select
      value={value}
      onChange={e => onChange(e.target.value)}
      className="flex-1 px-2 py-1 bg-surface-raised border border-border-default rounded text-xs text-zinc-300 outline-none focus:border-brand-orange/50 min-w-0"
    >
      <option value="">{placeholder}</option>
      {lockers.map(l => (
        <option key={l.id} value={l.id}>📦 {l.name}</option>
      ))}
      {sortedBins.map(b => {
        const depth = indent(b.id)
        const prefix = '  '.repeat(depth) + '└ '
        return <option key={b.id} value={b.id}>{prefix}{b.name}</option>
      })}
    </select>
  )
}

// ── Gear item row ─────────────────────────────────────────────────────────────
function GearItemRow({
  item, targets, resolution, onResolve
}: {
  item: GearItemNode
  targets: Container[]
  resolution: Resolution | null
  onResolve: (r: Resolution | null) => void
}) {
  return (
    <div className={`flex items-center gap-2 py-1.5 pl-3 pr-2 rounded-md ${resolution ? 'opacity-60' : ''}`}>
      <IconBox size={12} className="text-zinc-500 shrink-0" />
      <span className="text-xs text-zinc-300 flex-1 truncate min-w-0">
        {item.title}{item.brand ? ` · ${item.brand}` : ''}
      </span>
      {resolution ? (
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] text-zinc-500 italic">
            {resolution.action === 'delete' ? 'Will be deleted' : 'Will be moved'}
          </span>
          <button onClick={() => onResolve(null)}
            className="text-[10px] text-zinc-600 hover:text-zinc-300 underline">
            undo
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-1.5 shrink-0">
          <ContainerPicker value="" targets={targets} placeholder="Move to..."
            onChange={id => id && onResolve({ action: 'move', targetId: id })} />
          <button onClick={() => onResolve({ action: 'delete' })}
            className="p-1 rounded text-zinc-600 hover:text-red-400 hover:bg-surface-base transition-colors"
            title="Delete item">
            <IconTrash size={12} />
          </button>
        </div>
      )}
    </div>
  )
}

// ── Bin row (recursive) ───────────────────────────────────────────────────────
function BinRow({
  bin, targets, binResolution, onResolveBin, itemResolutions, onResolveItem, depth = 0
}: {
  bin: ContainerNode
  targets: Container[]
  binResolution: Resolution | null
  onResolveBin: (id: string, r: Resolution | null) => void
  itemResolutions: Map<string, Resolution | null>
  onResolveItem: (id: string, r: Resolution | null) => void
  depth?: number
}) {
  const [expanded, setExpanded] = useState(true)
  const hasContents = bin.bins.length > 0 || bin.gearItems.length > 0
  const indent = depth * 12

  return (
    <div>
      <div className={`flex items-center gap-2 py-1.5 pr-2 rounded-md ${binResolution ? 'opacity-60' : 'hover:bg-surface-raised/50'}`}
        style={{ paddingLeft: `${indent + 8}px` }}>
        {hasContents && !binResolution ? (
          <button onClick={() => setExpanded(v => !v)} className="shrink-0">
            {expanded
              ? <IconChevronDown size={11} className="text-zinc-500" />
              : <IconChevronRight size={11} className="text-zinc-500" />}
          </button>
        ) : <span className="w-[11px] shrink-0" />}
        <IconLayoutBottombar size={12} className="text-zinc-400 shrink-0" />
        <span className="text-xs text-zinc-200 flex-1 truncate font-medium">{bin.name}</span>
        {binResolution ? (
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-[10px] text-zinc-500 italic">
              {binResolution.action === 'move' ? 'Will move with contents' : 'Will be deleted'}
            </span>
            <button onClick={() => onResolveBin(bin.id, null)}
              className="text-[10px] text-zinc-600 hover:text-zinc-300 underline">undo</button>
          </div>
        ) : (
          <div className="flex items-center gap-1.5 shrink-0">
            <span className="text-[10px] text-zinc-500 mr-1">Move gear bag with contents →</span>
            <ContainerPicker value="" targets={targets} placeholder="Select location..."
              onChange={id => id && onResolveBin(bin.id, { action: 'move', targetId: id })} />
            <button onClick={() => onResolveBin(bin.id, { action: 'delete' })}
              className="p-1 rounded text-zinc-600 hover:text-red-400 hover:bg-surface-base transition-colors"
              title="Delete gear bag and all contents">
              <IconTrash size={12} />
            </button>
          </div>
        )}
      </div>

      {/* Children — only shown if bin itself is not resolved */}
      {expanded && !binResolution && (
        <div>
          {bin.gearItems.map(item => (
            <div key={item.id} style={{ paddingLeft: `${indent + 20}px` }}>
              <GearItemRow
                item={item} targets={targets}
                resolution={itemResolutions.get(item.id) ?? null}
                onResolve={r => onResolveItem(item.id, r)} />
            </div>
          ))}
          {bin.bins.map(childBin => (
            <BinRow key={childBin.id} bin={childBin} targets={targets}
              binResolution={itemResolutions.get(childBin.id) ?? null}
              onResolveBin={onResolveBin}
              itemResolutions={itemResolutions}
              onResolveItem={onResolveItem}
              depth={depth + 1} />
          ))}
        </div>
      )}
    </div>
  )
}

// ── Collect all IDs that need resolution ─────────────────────────────────────
function collectIds(node: ContainerNode): { binIds: string[]; itemIds: string[] } {
  const binIds: string[] = []
  const itemIds: string[] = []

  function walk(n: ContainerNode) {
    for (const item of n.gearItems) itemIds.push(item.id)
    for (const bin of n.bins) {
      binIds.push(bin.id)
      walk(bin)
    }
  }
  walk(node)
  return { binIds, itemIds }
}

// ── Main modal ────────────────────────────────────────────────────────────────
export function ContainerReassignModal({ container, allContainers, onResolved, onCancel }: Props) {
  const { error } = useToast()
  const [contents, setContents] = useState<ContainerNode | null>(null)
  const [loading, setLoading] = useState(true)
  const [applying, setApplying] = useState(false)
  const [resolutions, setResolutions] = useState<Map<string, Resolution | null>>(new Map())

  const validTargets = getValidTargets(allContainers, container.id)

  useEffect(() => {
    containersApi.contents(container.id).then(res => {
      setContents(res.data)
      setLoading(false)
    }).catch(() => {
      setLoading(false)
    })
  }, [container.id])

  const setResolution = (id: string, r: Resolution | null) => {
    setResolutions(prev => {
      const next = new Map(prev)
      next.set(id, r)
      return next
    })
  }

  // Check if all required IDs are resolved
  const allResolved = contents ? (() => {
    const { binIds, itemIds } = collectIds(contents)
    // A bin being moved resolves itself AND all its descendants
    const resolvedBins = new Set<string>()

    const checkBin = (nodeId: string): boolean => {
      if (resolvedBins.has(nodeId)) return true
      const r = resolutions.get(nodeId)
      if (r) { resolvedBins.add(nodeId); return true }
      return false
    }

    // Mark all descendants of moved bins as resolved
    for (const binId of binIds) {
      if (resolutions.get(binId)) {
        // Mark all descendants
        const markDescendants = (id: string) => {
          resolvedBins.add(id)
          const node = findNode(contents, id)
          if (node) {
            for (const child of node.bins) markDescendants(child.id)
          }
        }
        markDescendants(binId)
      }
    }

    for (const binId of binIds) {
      if (!resolvedBins.has(binId)) return false
    }
    // Check items not inside a resolved bin
    for (const itemId of (contents.gearItems ?? []).map(i => i.id)) {
      if (!resolutions.get(itemId)) return false
    }
    return true
  })() : false

  const findNode = (node: ContainerNode, id: string): ContainerNode | null => {
    if (node.id === id) return node
    for (const child of node.bins) {
      const found = findNode(child, id)
      if (found) return found
    }
    return null
  }

  const handleApply = async () => {
    if (!contents) return
    setApplying(true)
    try {
      // Process bin moves/deletes first (top-level bins first)
      const { binIds, itemIds } = collectIds(contents)
      const resolvedBins = new Set<string>()

      for (const binId of binIds) {
        if (resolvedBins.has(binId)) continue
        const r = resolutions.get(binId)
        if (!r) continue
        if (r.action === 'move') {
          await containersApi.move(binId, r.targetId)
          // Mark all descendants as handled
          const markDescendants = (id: string) => {
            resolvedBins.add(id)
            const node = findNode(contents, id)
            if (node) node.bins.forEach(c => markDescendants(c.id))
          }
          markDescendants(binId)
        } else if (r.action === 'delete') {
          // Recursively delete — API handles contents guard, so delete leaf-first
          const deleteRecursive = async (id: string) => {
            const node = findNode(contents, id)
            if (node) {
              for (const child of node.bins) await deleteRecursive(child.id)
              for (const item of node.gearItems) await gearItemsApi.delete(item.id)
            }
            await containersApi.delete(id)
          }
          const markDescendants = (id: string) => {
            resolvedBins.add(id)
            const node = findNode(contents, id)
            if (node) node.bins.forEach(c => markDescendants(c.id))
          }
          await deleteRecursive(binId)
          markDescendants(binId)
        }
      }

      // Process direct gear item moves/deletes
      for (const itemId of (contents.gearItems ?? []).map(i => i.id)) {
        const r = resolutions.get(itemId)
        if (!r) continue
        if (r.action === 'move') {
          await gearItemsApi.update(itemId, { containerId: r.targetId })
        } else {
          await gearItemsApi.delete(itemId)
        }
      }

      onResolved()
    } catch (e: any) {
      error('Failed to apply changes', e.message)
    } finally {
      setApplying(false)
    }
  }

  return (
    <div className="fixed inset-0 z-[300] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
      <div className="w-full max-w-2xl bg-surface-overlay border border-border-default rounded-2xl shadow-2xl flex flex-col max-h-[80vh]">
        {/* Header */}
        <div className="px-6 py-4 border-b border-border-default shrink-0">
          <h2 className="text-base font-semibold text-zinc-100">
            {container.isTopLevel ? 'Empty locker before deleting' : 'Resolve contents before deleting'}
          </h2>
          <p className="text-sm text-zinc-400 mt-1">
            <span className="font-medium text-zinc-200">"{container.name}"</span> contains gear and gear bags.
            Move or delete everything inside before it can be deleted.
          </p>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 py-3">
          {loading ? (
            <div className="py-8 text-center text-sm text-zinc-500">Loading contents...</div>
          ) : !contents ? (
            <div className="py-8 text-center text-sm text-zinc-500">Failed to load contents</div>
          ) : (
            <div className="space-y-0.5">
              {/* Direct gear items */}
              {(contents.gearItems ?? []).map(item => (
                <GearItemRow key={item.id} item={item} targets={validTargets}
                  resolution={resolutions.get(item.id) ?? null}
                  onResolve={r => setResolution(item.id, r)} />
              ))}
              {/* Bins */}
              {(contents.bins ?? []).map(bin => (
                <BinRow key={bin.id} bin={bin} targets={validTargets}
                  binResolution={resolutions.get(bin.id) ?? null}
                  onResolveBin={(id, r) => setResolution(id, r)}
                  itemResolutions={resolutions}
                  onResolveItem={(id, r) => setResolution(id, r)} />
              ))}
              {(contents.gearItems?.length === 0 && contents.bins?.length === 0) && (
                <div className="py-6 text-center text-sm text-zinc-500">Container is empty</div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t border-border-default flex items-center justify-between shrink-0">
          <div className="text-xs text-zinc-500">
            {allResolved
              ? <span className="text-emerald-400">✓ All contents resolved — ready to delete</span>
              : 'Resolve all items and gear bags above to continue'}
          </div>
          <div className="flex items-center gap-2">
            <button onClick={onCancel}
              className="px-4 py-2 rounded-lg text-sm text-zinc-400 hover:text-zinc-200 hover:bg-surface-raised transition-colors">
              Cancel
            </button>
            <button onClick={handleApply} disabled={!allResolved || applying}
              className="px-4 py-2 rounded-lg text-sm font-medium bg-red-900 text-red-200 hover:bg-red-800 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
              {applying ? 'Applying...' : 'Apply & delete container'}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
